/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 4: Non-Breaking Hyphens (TAK-only)
// ═══════════════════════════════════════════════════════════════════════════

import {
  safeAlert, reportResult, rangeDetail,
  RunDetail, SRS_NAV_PREFIX, MAX_NAV_ITEMS
} from '../runs/common';
import { getConfig } from '../taskpane/configService';

const REGULAR_HYPHEN     = "-";       // U+002D
const NONBREAKING_HYPHEN = "\u2011";  // U+2011

/** Bookmark prefix for this run */
const BM_PREFIX = SRS_NAV_PREFIX + "R4_";

/** Snapshot of a range's font, captured before replacement so it can be
 *  restored after Word auto-assigns Cambria Math to the U+2011 run. */
interface FontSnapshot {
  name:      string;
  size:      number;
  bold:      boolean;
  italic:    boolean;
  color:     string;
  underline: Word.Font["underline"];
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 1: Collect unique product codes from body + tables
// ─────────────────────────────────────────────────────────────────────────

/** Load every table cell's text in two batched syncs (rows → cells → text). */
async function loadTableCellText(
  context: Word.RequestContext,
  tables: Word.TableCollection
): Promise<void> {
  for (const t of tables.items)
    for (const r of t.rows.items) r.cells.load("items");
  await context.sync();

  for (const t of tables.items)
    for (const r of t.rows.items)
      for (const c of r.cells.items) c.body.load("text");
  await context.sync();
}

/** Flatten all table cell texts into one array (cell text must be loaded first). */
function collectTableCellTexts(tables: Word.TableCollection): string[] {
  const texts: string[] = [];
  for (const t of tables.items)
    for (const r of t.rows.items)
      for (const c of r.cells.items) texts.push(c.body.text || "");
  return texts;
}

/** Run the (global) product-code regex over one string and add hyphenated hits. */
function extractCodesInto(text: string, productCodeRegex: RegExp, out: Set<string>): void {
  const safe = text || "";
  productCodeRegex.lastIndex = 0;

  let match = productCodeRegex.exec(safe);
  while (match !== null) {
    if (match[0].includes(REGULAR_HYPHEN)) {
      out.add(match[0]);
    }
    // Defensive: a zero-width match would not advance lastIndex on its own.
    if (match.index === productCodeRegex.lastIndex) {
      productCodeRegex.lastIndex++;
    }
    match = productCodeRegex.exec(safe);
  }
}

async function collectUniqueProductCodes(
  context: Word.RequestContext,
  productCodeRegex: RegExp
): Promise<Set<string>> {
  // Batch load paragraphs + tables together in one sync
  const paragraphs = context.document.body.paragraphs;
  const tables     = context.document.body.tables;
  paragraphs.load("items");
  tables.load("items");
  await context.sync();

  for (const p of paragraphs.items) p.load("text");
  for (const t of tables.items)     t.rows.load("items");
  await context.sync();

  await loadTableCellText(context, tables);

  // Gather every text string (paragraphs + table cells), then scan each one.
  const allTexts = paragraphs.items.map(p => p.text || "");
  allTexts.push(...collectTableCellTexts(tables));

  const uniqueCodes = new Set<string>();
  for (const text of allTexts) extractCodesInto(text, productCodeRegex, uniqueCodes);

  return uniqueCodes;
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 2: Replace hyphens + fix font + insert navigation bookmarks
// ─────────────────────────────────────────────────────────────────────────

/** Restore the captured font on every sub-run produced by insertText.
 *  After insertText, Word may split the range into multiple internal runs
 *  (one for regular chars, one for U+2011 which gets Cambria Math).
 *  getTextRanges([], false) gets ALL sub-runs so we fix every one. */
async function restoreFontsAfterReplace(
  context: Word.RequestContext,
  ranges: Word.Range[],
  fontSnapshots: FontSnapshot[]
): Promise<void> {
  for (let i = 0; i < ranges.length; i++) {
    const snap = fontSnapshots[i];

    const subRanges = ranges[i].getTextRanges([], false);
    subRanges.load("items");
    await context.sync();

    for (const sub of subRanges.items) {
      sub.font.name      = snap.name;
      sub.font.size      = snap.size;
      sub.font.bold      = snap.bold;
      sub.font.italic    = snap.italic;
      sub.font.color     = snap.color;
      sub.font.underline = snap.underline;
    }
    await context.sync();
  }
}

async function replaceProductCodeHyphens(
  uniqueCodes: Set<string>,
  context: Word.RequestContext,
  navDetails: RunDetail[]
): Promise<{ totalOccurrences: number; totalHyphens: number }> {
  let totalOccurrences = 0;
  let totalHyphens     = 0;
  let navCount         = 0;

  // ── Why we sort by length DESCENDING ──────────────────────────────────
  // body.search is a strict codepoint match — U+002D (regular hyphen) and
  // U+2011 (non-breaking hyphen) are distinct characters. If we processed
  // shorter codes first, the replacement of "TAK-925" would also convert
  // the first hyphen of every "TAK-925-1023" occurrence to U+2011, leaving
  // the document text as "TAK‑925-1023". The subsequent literal search for
  // "TAK-925-1023" would then return zero hits and the trailing "-1023"
  // segment would never get converted.
  //
  // Going longest-first prevents this: "TAK-925-1023" is fully replaced
  // first; afterwards, those positions read "TAK‑925‑1023" and no longer
  // match the literal "TAK-925" search, so only genuinely standalone
  // occurrences of "TAK-925" are picked up by the next iteration.
  const sortedCodes = Array.from(uniqueCodes).sort((a, b) => b.length - a.length);

  console.log(`  Processing ${sortedCodes.length} unique code(s), longest first:`);
  for (const c of sortedCodes) console.log(`    • ${c} (len=${c.length})`);
  console.log("");

  for (const code of sortedCodes) {
    const replacement = code.replace(/-/g, NONBREAKING_HYPHEN);
    const hyphenCount = (code.match(/-/g) || []).length;

    console.log(`  "${code}" → "${replacement}"`);

    const foundRanges = context.document.body.search(code, {
      matchCase: true,
      matchWholeWord: false,
    });
    foundRanges.load("items");
    await context.sync();

    if (foundRanges.items.length === 0) {
      console.log(`    0 occurrence(s) — skipped`);
      continue;
    }

    // ── STEP 1: Snapshot font BEFORE replacement ─────────────────────────
    // U+2011 causes Word to auto-assign Cambria Math to the run.
    // We capture the original font here so we can restore it after.
    for (const range of foundRanges.items) {
      range.font.load("name, size, bold, italic, color, underline");
    }
    await context.sync();

    const fontSnapshots: FontSnapshot[] = foundRanges.items.map(range => ({
      name:      range.font.name,
      size:      range.font.size,
      bold:      range.font.bold,
      italic:    range.font.italic,
      color:     range.font.color,
      underline: range.font.underline,
    }));

    console.log(`    ${foundRanges.items.length} occurrence(s) | font: "${fontSnapshots[0].name}" ${fontSnapshots[0].size}pt`);

    // ── STEP 2: tracking ON → insertText + bookmark (tracked change) ─────
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();

    for (const range of foundRanges.items) {
      // Replace regular hyphens with non-breaking hyphens (tracked)
      range.insertText(replacement, Word.InsertLocation.replace);

      // Navigation bookmark
      if (navCount < MAX_NAV_ITEMS) {
        const bmName = BM_PREFIX + String(navCount).padStart(3, "0");
        range.insertBookmark(bmName);
        navDetails.push(rangeDetail(bmName, code, "→ non-breaking hyphens"));
        navCount++;
      }
    }
    await context.sync();

    // ── STEP 3: tracking OFF → restore font on ALL sub-runs (not tracked) ─
    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();

    await restoreFontsAfterReplace(context, foundRanges.items, fontSnapshots);

    totalOccurrences += foundRanges.items.length;
    totalHyphens     += foundRanges.items.length * hyphenCount;
  }

  return { totalOccurrences, totalHyphens };
}

// ─────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────

export async function run4() {
  await Word.run(async (context) => {
    try {
      const cfg                = getConfig().run4_hyphens;
      const productCodePrefix  = cfg.productCodePrefix;
      const productCodeRegex   = new RegExp(cfg.productCodeRegex, "g");
      const replaceWithNonBreaking = cfg.replaceWithNonBreaking;

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("=".repeat(70));
      console.log(`=== NON-BREAKING HYPHEN CHECKER (${productCodePrefix} codes) ===`);
      console.log("=".repeat(70));
      console.log(`Pattern: ${cfg.productCodeRegex}`);
      console.log("Go To:   Bookmark on each replaced range\n");

      const uniqueCodes = await collectUniqueProductCodes(context, productCodeRegex);

      console.log(`Unique ${productCodePrefix} product codes found: ${uniqueCodes.size}`);

      if (uniqueCodes.size === 0) {
        reportResult(`Non-Breaking Hyphens — no changes needed`, [
          { label: `No ${productCodePrefix} codes requiring conversion found`, note: 'No changes made' }
        ]);
        return;
      }

      if (!replaceWithNonBreaking) {
        reportResult(`Non-Breaking Hyphens — replacement disabled`, [
          { label: `${productCodePrefix} codes found`, count: uniqueCodes.size },
          { label: 'Replacement skipped', note: 'replaceWithNonBreaking is false in profile config' },
        ]);
        return;
      }

      const navDetails: RunDetail[] = [];
      const { totalOccurrences, totalHyphens } =
        await replaceProductCodeHyphens(uniqueCodes, context, navDetails);

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("\n" + "=".repeat(70));
      console.log(`Unique ${productCodePrefix} codes:  ${uniqueCodes.size}`);
      console.log(`Total occurrences fixed: ${totalOccurrences}`);
      console.log(`Total hyphens replaced:  ${totalHyphens}`);
      console.log(`Go To bookmarks placed:  ${navDetails.length}`);
      console.log(`Font restored silently (not tracked)`);
      console.log("=".repeat(70));

      if (totalOccurrences > 0) {
        reportResult('Non-Breaking Hyphens — changes made', [
          { label: `Unique ${productCodePrefix} codes found`, count: uniqueCodes.size },
          { label: 'Occurrences fixed', count: totalOccurrences },
          { label: 'Individual hyphens replaced', count: totalHyphens },
          { label: 'Font restored (Cambria Math corrected)', note: 'not tracked' },
          ...navDetails,
        ]);
      } else {
        reportResult('Non-Breaking Hyphens — no changes needed', [
          { label: `No ${productCodePrefix} codes requiring conversion found`, note: 'No changes made' }
        ]);
      }
    } catch (error) {
      console.error("Error in run4:", error);
      safeAlert("An error occurred during the non-breaking hyphen check.");
    }
  });
}
